// Low-poly stylized animal part templates. The FRONT of every body faces +Z.
// Each part becomes ONE InstancedMesh shared across the whole herd (draw calls = parts).
const p = (shape, args, pos, color, extra = {}) => ({ shape, args, pos, color, ...extra });

const legs = (xo, zo, len, r, color) => [
  p('cyl', [r, r, len, 5], [xo, len / 2, zo], color),
  p('cyl', [r, r, len, 5], [-xo, len / 2, zo], color),
  p('cyl', [r, r, len, 5], [xo, len / 2, -zo], color),
  p('cyl', [r, r, len, 5], [-xo, len / 2, -zo], color),
];

export const SPECIES = {
  // ---- Land quadrupeds ----
  donkey: [
    p('box', [0.95, 1.05, 2.2], [0, 1.05, 0], '#5b6b82', { shadow: true }),
    p('box', [0.65, 0.8, 0.75], [0, 1.55, 1.2], '#6b7c94'),
    p('box', [0.15, 0.5, 0.15], [0.22, 2.05, 1.3], '#41506a'),
    p('box', [0.15, 0.5, 0.15], [-0.22, 2.05, 1.3], '#41506a'),
    ...legs(0.35, 0.8, 0.95, 0.13, '#3f4a5e'),
    p('cyl', [0.06, 0.02, 0.7, 4], [0, 1.2, -1.2], '#2f3847', { rot: [0.5, 0, 0] }),
  ],
  elephant: [
    p('box', [1.9, 2.2, 3.6], [0, 2.0, 0], '#9a9a9a', { shadow: true, roughness: 1 }),
    p('box', [1.3, 1.4, 1.3], [0, 2.3, 2.0], '#8f8f8f'),
    p('cyl', [0.28, 0.18, 2, 6], [0, 1.3, 2.9], '#8a8a8a', { rot: [Math.PI / 2 + 0.5, 0, 0] }),
    p('box', [0.2, 1.4, 1.1], [1.0, 2.5, 1.6], '#7f7f7f'),
    p('box', [0.2, 1.4, 1.1], [-1.0, 2.5, 1.6], '#7f7f7f'),
    ...legs(0.7, 1.1, 1.5, 0.34, '#828282'),
  ],
  jaguar: [
    p('box', [0.7, 0.75, 2.6], [0, 0.9, 0], '#d9a441', { shadow: true }),
    p('box', [0.6, 0.6, 0.7], [0, 1.1, 1.5], '#e2b155'),
    ...legs(0.28, 1.0, 0.75, 0.11, '#b6862f'),
    p('cyl', [0.09, 0.05, 1.4, 5], [0, 1.1, -1.5], '#d9a441', { rot: [0.7, 0, 0] }),
  ],
  lion: [
    p('box', [0.9, 0.9, 2.2], [0, 0.95, 0], '#c98a3a', { shadow: true }),
    p('sphere', [0.8, 10, 8], [0, 1.2, 1.0], '#7a4a1e', { roughness: 1 }),
    p('sphere', [0.5, 10, 8], [0, 1.25, 1.35], '#d99a45'),
    ...legs(0.35, 0.75, 0.9, 0.13, '#b6862f'),
    p('cyl', [0.07, 0.04, 1.3, 5], [0, 1.0, -1.3], '#c98a3a', { rot: [0.7, 0, 0] }),
  ],
  tiger: [
    p('box', [0.8, 0.8, 2.3], [0, 0.9, 0], '#e07b2a', { shadow: true }),
    p('sphere', [0.5, 10, 8], [0, 1.15, 1.25], '#e8863a'),
    p('box', [0.85, 0.12, 0.18], [0, 1.32, 0.5], '#2a1a12'),
    p('box', [0.85, 0.12, 0.18], [0, 1.32, 0.0], '#2a1a12'),
    p('box', [0.85, 0.12, 0.18], [0, 1.32, -0.5], '#2a1a12'),
    ...legs(0.32, 0.8, 0.85, 0.12, '#c96a20'),
    p('cyl', [0.06, 0.03, 1.3, 5], [0, 0.95, -1.35], '#e07b2a', { rot: [0.7, 0, 0] }),
  ],
  bear: [
    p('box', [1.2, 1.2, 2.0], [0, 1.1, 0], '#4a3527', { shadow: true }),
    p('sphere', [0.6, 10, 8], [0, 1.5, 1.1], '#523b2b'),
    p('sphere', [0.18, 6, 6], [0.3, 2.0, 1.1], '#3a2a1e'),
    p('sphere', [0.18, 6, 6], [-0.3, 2.0, 1.1], '#3a2a1e'),
    ...legs(0.45, 0.7, 0.7, 0.22, '#3f2d20'),
  ],
  caiman: [
    p('box', [0.7, 0.45, 2.6], [0, 0.28, 0], '#33452f', { shadow: true }),
    p('box', [0.45, 0.28, 1.1], [0, 0.26, 1.7], '#3c4f34'),
    p('cyl', [0.24, 0.05, 1.8, 4], [0, 0.28, -1.9], '#2b3a27', { rot: [Math.PI / 2, 0, 0] }),
    ...legs(0.5, 0.9, 0.28, 0.09, '#2b3a27'),
  ],

  // ---- Canopy / flying ----
  macaw: [
    p('sphere', [0.35, 8, 6], [0, 0, 0], '#e63946', { scale: [1, 1, 1.6] }),
    p('sphere', [0.2, 8, 6], [0, 0.15, 0.55], '#f1c40f'),
    p('box', [1.5, 0.08, 0.5], [0, 0.05, -0.1], '#2a6df4'),
    p('cyl', [0.05, 0.02, 0.9, 4], [0, 0, -0.85], '#2a9d8f', { rot: [Math.PI / 2, 0, 0] }),
  ],
  anaconda: [
    p('sphere', [0.4, 8, 6], [0, 0, 0.9], '#3a5a34'),
    p('sphere', [0.42, 8, 6], [0, 0, 0.3], '#436a3c'),
    p('sphere', [0.42, 8, 6], [0, 0, -0.3], '#3a5a34'),
    p('sphere', [0.4, 8, 6], [0, 0, -0.9], '#436a3c'),
    p('sphere', [0.3, 8, 6], [0, 0.1, 1.4], '#2f4a2b'),
  ],
  sloth: [
    p('sphere', [0.45, 8, 6], [0, 0, 0], '#8a9a7a', { scale: [1, 1.3, 1] }),
    p('sphere', [0.3, 8, 6], [0, -0.5, 0], '#9aab88'),
    p('cyl', [0.1, 0.1, 0.8, 4], [0.35, 0.35, 0], '#6f7d61', { rot: [0, 0, 0.4] }),
    p('cyl', [0.1, 0.1, 0.8, 4], [-0.35, 0.35, 0], '#6f7d61', { rot: [0, 0, -0.4] }),
  ],
  howler: [
    p('sphere', [0.5, 8, 6], [0, 0, 0], '#3a2c22'),
    p('sphere', [0.3, 8, 6], [0, 0.5, 0.2], '#4a3a2c'),
    p('cyl', [0.08, 0.08, 0.7, 4], [-0.35, -0.3, 0], '#2c211a'),
    p('cyl', [0.08, 0.08, 0.7, 4], [0.35, -0.3, 0], '#2c211a'),
  ],

  // ---- Water ----
  dolphin: [
    p('sphere', [0.7, 10, 8], [0, 0, 0], '#9fb3c8', { scale: [1, 0.7, 2.4], metalness: 0.3, roughness: 0.5, shadow: true }),
    p('cone', [0.25, 0.8, 4], [0, 0.5, -0.2], '#8aa0b6'),
    p('cone', [0.5, 0.6, 4], [0, 0, -1.7], '#8aa0b6', { rot: [Math.PI / 2, 0, 0] }),
  ],
  shark: [
    p('sphere', [0.9, 10, 8], [0, 0, 0], '#6b7280', { scale: [1, 0.9, 3], roughness: 0.6, shadow: true }),
    p('cone', [0.3, 1.1, 4], [0, 0.8, 0.2], '#5b626e'),
    p('cone', [0.7, 1.0, 4], [0, 0, -2.4], '#5b626e', { rot: [Math.PI / 2, 0, 0] }),
    p('sphere', [0.14, 6, 6], [0.3, 0.2, 1.6], '#ffb300', { emissive: '#ffb300', ei: 2.4 }),
    p('sphere', [0.14, 6, 6], [-0.3, 0.2, 1.6], '#ffb300', { emissive: '#ffb300', ei: 2.4 }),
  ],
  piranha: [
    p('sphere', [0.6, 8, 6], [0, 0, 0], '#8a94a0', { scale: [0.35, 0.6, 1], metalness: 0.4, roughness: 0.4 }),
    p('cone', [0.3, 0.6, 4], [0, 0, -0.55], '#5a636e', { rot: [Math.PI / 2, 0, 0] }),
    p('sphere', [0.2, 6, 4], [0, -0.12, 0.4], '#b83b2e', { scale: [0.6, 0.4, 1] }),
  ],
  otter: [
    p('sphere', [0.45, 8, 6], [0, 0, 0], '#6b4a2f', { scale: [0.8, 0.7, 1.8], shadow: true }),
    p('sphere', [0.28, 8, 6], [0, 0.15, 0.7], '#7a5636'),
    p('cone', [0.18, 0.8, 4], [0, 0, -1.0], '#5a3e28', { rot: [Math.PI / 2, 0, 0] }),
  ],

  // Whales (Mega-Donors) — massive, slow, occasionally breaching
  whale: [
    p('sphere', [3.2, 12, 10], [0, 0, 0], '#41505f', { scale: [1, 0.85, 2.4], shadow: true, roughness: 0.7, metalness: 0.1 }),
    p('sphere', [1.3, 10, 8], [0, 1.3, -3.4], '#39485a'),
    p('cone', [1.0, 2.2, 4], [0, 0.9, -7.0], '#39485a'),
    p('box', [3.8, 0.28, 1.2], [0, 0.9, -7.9], '#2f3c4a'),
    p('sphere', [0.24, 6, 6], [1.15, 0.5, 4.4], '#0b0f14'),
    p('sphere', [0.24, 6, 6], [-1.15, 0.5, 4.4], '#0b0f14'),
  ],
  // Electric Eels (Info Brokers) — glowing, darting along the banks
  eel: [
    p('sphere', [0.22, 6, 6], [0, 0, 0.8], '#2fe6c8', { emissive: '#2fe6c8', ei: 2.2 }),
    p('sphere', [0.24, 6, 6], [0, 0, 0.3], '#59f0d8', { emissive: '#59f0d8', ei: 2.4 }),
    p('sphere', [0.24, 6, 6], [0, 0, -0.2], '#2fe6c8', { emissive: '#2fe6c8', ei: 2.2 }),
    p('sphere', [0.22, 6, 6], [0, 0, -0.7], '#59f0d8', { emissive: '#59f0d8', ei: 2.4 }),
    p('sphere', [0.18, 6, 6], [0, 0, -1.2], '#2fe6c8', { emissive: '#2fe6c8', ei: 2.0 }),
    p('sphere', [0.2, 6, 6], [0, 0.05, 1.25], '#124a44', { emissive: '#0e6b5f', ei: 1.0 }),
  ],
  // Goldfish (The Distracted Public) — tiny, dense schools in the shallows
  goldfish: [
    p('sphere', [0.16, 6, 5], [0, 0, 0], '#ff9e2c', { scale: [0.7, 0.9, 1.5], emissive: '#e06a10', ei: 0.4 }),
    p('cone', [0.12, 0.18, 4], [0, 0, -0.22], '#ffb84d', { rot: [Math.PI / 2, 0, 0] }),
  ],
};
